import Tooltip from '@mui/material/Tooltip';
import DeleteIcon from 'assets/svg/close-icon.svg';
import DocXlsxIcon from 'assets/svg/icon-others-documents.svg';
import { ConfirmDialog } from 'components/Dialog/ConfirmDialog/ConfirmDialog';
import { useMutateInspection } from 'hooks/Inspections/InspectionsHooks';
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import DocumentService from 'services/DocumentService/documentService';
import PhotoService from 'services/PhotoService/photoService';
import { getErrorMessage, hasException } from 'utils/exceptions/formatDeleteFileException';
import { IDocument, IPhoto } from '../../../Inspection/Inspection.types';
import PhotoGallery from '../PhotoGallery/PhotoGallery';
import TabEmpty from '../TabEmpty/TabEmpty';
import * as S from './TabRegulation.styles';
import { ITabRegulationProps } from './TabRegulation.types';

const TabRegulation: React.FC<ITabRegulationProps> = ({ inspection }) => {
  const { id: inspectionId, photos, files } = inspection;
  const [selectedPhoto, setSelectedPhoto] = useState<IPhoto>();
  const [selectedDocument, setSelectedDocument] = useState<IDocument>();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [deleteType, setDeleteType] = useState<'photo' | 'document'>('photo');

  const { invalidateInspectionParallel } = useMutateInspection();

  // Filtrar fotos e documentos com origin === 'UPLOAD_KITE_REGULATION'
  const regulationPhotos = photos?.filter((photo) => photo.origin === 'UPLOAD_KITE_REGULATION') || [];
  const regulationDocuments = files?.filter((file) => file.origin === 'UPLOAD_KITE_REGULATION') || [];

  const hasRegulationFiles = regulationPhotos.length > 0 || regulationDocuments.length > 0;

  const handleDocumentDeletion = (event: React.MouseEvent<HTMLElement>, document: IDocument) => {
    event.stopPropagation();
    setSelectedDocument(document);
    setDeleteType('document');
    setShowConfirmDialog(true);
  };

  const deleteFile = async () => {
    if (deleteType === 'photo' && selectedPhoto?.id) {
      PhotoService.deletePhoto(selectedPhoto)
        .then(() => {
          invalidateInspectionParallel(inspectionId);
          toast.success('Foto excluída com sucesso!');
          setShowConfirmDialog(false);
        })
        .catch((error) => {
          const exception = error.response?.data?.exception;
          if (exception && hasException(exception)) {
            toast.warning(getErrorMessage(exception));
          } else {
            toast.error('Erro ao excluir foto!');
          }
        });
    } else if (deleteType === 'document' && selectedDocument?.id) {
      DocumentService.deleteDocuments([selectedDocument.id], inspectionId)
        .then(() => {
          invalidateInspectionParallel(inspectionId);
          toast.success('Documento excluído com sucesso!');
          setShowConfirmDialog(false);
        })
        .catch((error) => {
          const exception = error.response?.data?.exception;
          if (exception && hasException(exception)) {
            toast.warning(getErrorMessage(exception));
          } else {
            toast.error('Erro ao excluir documento!');
          }
        });
    }
  };

  const downloadDocument = (file: IDocument) => {
    DocumentService.downloadDocument(file);
  };

  if (!hasRegulationFiles) {
    return <TabEmpty title="Não há arquivos de regulação" />;
  }

  const inspectionWithFilteredPhotos = {
    ...inspection,
    photos: regulationPhotos,
  };

  return (
    <>
      <S.TabRegulationContainer>
        {regulationPhotos.length > 0 && (
          <div>
            <S.SectionTitle>Fotos de Regulação ({regulationPhotos.length})</S.SectionTitle>
            <S.FilesGrid>
              <PhotoGallery inspection={inspectionWithFilteredPhotos} />
            </S.FilesGrid>
          </div>
        )}

        {regulationDocuments.length > 0 && (
          <div>
            <S.SectionTitle>Documentos de Regulação ({regulationDocuments.length})</S.SectionTitle>
            <S.FilesGrid>
              {regulationDocuments.map((file) => (
                <S.DocumentItem key={file.id} onClick={() => downloadDocument(file)}>
                  <div onClick={() => downloadDocument(file)}>
                    <img src={DocXlsxIcon} alt="Ilustração de um documento" />
                  </div>
                  <Tooltip title={file.description} placement="top">
                    <S.FileTitle>{file.description}</S.FileTitle>
                  </Tooltip>
                  <Tooltip title="Excluir documento" placement="top">
                    <S.DeleteButton
                      data-testid="delete-doc-btn"
                      type="button"
                      onClick={(e) => handleDocumentDeletion(e, file)}
                    >
                      <img src={DeleteIcon} alt="Botão de excluir documento" />
                    </S.DeleteButton>
                  </Tooltip>
                </S.DocumentItem>
              ))}
            </S.FilesGrid>
          </div>
        )}
      </S.TabRegulationContainer>

      {showConfirmDialog && (
        <ConfirmDialog
          dialogVisibility={showConfirmDialog}
          handleClose={() => setShowConfirmDialog(false)}
          testID="confirm-dialog"
          onSubmit={deleteFile}
          title={`Deseja realmente excluir ${deleteType === 'photo' ? 'a foto' : 'o documento'}?`}
          subtitle="Ao confirmar, essa ação não poderá ser desfeita."
        />
      )}
    </>
  );
};

export default TabRegulation;
